import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Base64 } from 'js-base64';
import { v4 } from 'uuid';
import { formatMoneyCurrency } from '../../../../utils/currency';
import { useSelector } from 'react-redux';
import { hotelsActions, IHotelsState, ILoginState } from '@share/store/slices';
import { getWalletPrices, WalletPriceType } from '@utils';
import { IAccount, IUserWallet } from '@share/common-types';
import { Currency, WalletMessage } from '../../common';
import { setSelectedHotelReviewClientCash, setSelectedHotelReviewClientCashStr } from '@store/slices';
import { BlueButton } from '@share/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { renderNumber } from '@share/utils';
import "./style.scss";
import { Tooltip } from 'antd';
import { isEmpty } from 'lodash';

interface IMapStateToProps {
  loginStore: ILoginState;
}

const DashboardCards = ({
  adjustedPackageGroups,
  location,
  dateObject,
  roomsContent,
  rooms,
  sessionKey,
}: any) => {
  const history = useHistory();
  const [showDropdown, setShowDropdown] = useState(null);
  const [showAllRooms, setShowAllRooms] = useState(false);
  const dropdownRef = useRef<any>(null);
  const login = useSelector((state: IMapStateToProps) => state.loginStore);
  const hotelsStore = useSelector((state: { hotelsStore: IHotelsState }) => state.hotelsStore);
  const dispatch = useDispatch();

  const toggleDropdown = (roomKey: any) => {
    setShowDropdown((prevKey) => (prevKey === roomKey ? null : roomKey));
  };

  const findAmenities = (roomKey: any) => {
    const room = roomsContent.find((room: any) => {
      return room.roomKey === roomKey.targetRoomKey;
    });
    return room;
  };

  const findBedType = (roomKey: any) => {
    const room = roomsContent.find((room: any) => {
      return room.roomKey === roomKey.targetRoomKey;
    });
    return room;
  };
  const defaultRoomImage = 'https://portal.membergetaways.com/rsi/assets/not_image_room-CeAEfLM7.png';
  const findRoomImage = (roomKey: string) => {
    const room = roomsContent.find((room: any) => {
      return room.roomKey === roomKey;
    });
    return room
      ? room?.images[0]
      : defaultRoomImage;
  };

  const shouldShowGoldenBorder = (packageInfo: any): boolean => {
    if (!packageInfo || !packageInfo.rooms || packageInfo.rooms.length === 0) {
      return false;
    }

    const firstRoom = packageInfo.rooms[0];
    return (
      firstRoom?.customerLoyaltyIDRequiredAtReservation &&
      firstRoom?.paymentType?.toLowerCase() === 'prepay' &&
      packageInfo?.supplierId === 'TVP'
    );
  };

  const shouldShowGrayBorder = (packageInfo: any): boolean => {
    if (!packageInfo || !packageInfo.rooms || packageInfo.rooms.length === 0) {
      return false;
    }

    const firstRoom = packageInfo.rooms[0];
    return (
      firstRoom?.paymentType?.toLowerCase() === 'postpay' &&
      packageInfo?.supplierId === 'TVP'
    );
  };

  const renderPriceDifference = (packageInfo: any) => {
    if (!packageInfo) return null;

    const firstRoom = packageInfo.rooms?.[0];

    const customerLoyaltyRequired = firstRoom?.customerLoyaltyIDRequiredAtReservation;
    const priceDifferenceWithoutLoyalty = packageInfo.priceDifferenceWithOutLoyalty;
    const customerLoyalty = localStorage.getItem('GET_CUSTOMER_LOYALTY') ?? '';

    if (!customerLoyaltyRequired || !priceDifferenceWithoutLoyalty || priceDifferenceWithoutLoyalty <= 0 || isEmpty(customerLoyalty)) {
      return null;
    }

    const { account } = login;
    const currency = packageInfo.currency || account?.currency;

    return (
      <p className="hotel-card-wrapper__price-total-text" style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>
        <span style={{ fontSize: '11px', marginRight: '4px' }}>
          <Currency currency={currency as string} />
        </span>
        {renderNumber(priceDifferenceWithoutLoyalty, 2)}
      </p>
    );
  };

  const handleSubmit = (packageId: string, hotelId: string, walletPrices: any) => {
    const { account } = login;
    const { selectedHotelSearchClientCash } = hotelsStore;
    const displayWalletSavings = account?.walletWalletSavings && (walletPrices?.priceSavings > 0 || walletPrices?.maxClientCashAmountAllow > 0) && walletPrices?.maxClientCashAmountAllow;
    const selectedPropertyClientCash = displayWalletSavings ? walletPrices?.maxClientCashPermited : selectedHotelSearchClientCash?.selectedPropertyClientCash;

    dispatch(setSelectedHotelReviewClientCash(selectedPropertyClientCash));
    dispatch(setSelectedHotelReviewClientCashStr(selectedPropertyClientCash ? selectedPropertyClientCash.toString() : ''));
    dispatch(hotelsActions.setSelectedHotelSearchClientCash({ ...selectedHotelSearchClientCash, selectedPropertyReviewClientCash: selectedPropertyClientCash }));

    history.push(
      `/${login.account?.name}/book/${hotelId}/${packageId}?dates=${Base64.encode(
        JSON.stringify(dateObject),
      )}&&location=${Base64.encode(JSON.stringify(location))}&&rooms=${Base64.encode(
        JSON.stringify(rooms),
      )}&&sessionKey=${Base64.encode(JSON.stringify(sessionKey))}`,
    );
  };

  const findLowestPricePackage = (packages: any) => {
    if (!packages || packages.length === 0) return null;

    return packages.reduce((lowest: any, item: any) => {
      return item.price < lowest.price ? item : lowest;
    }, packages[0]);
  };

  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(null);
    }
  };

  useEffect(() => {
    if (showDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const roomsToDisplay = showAllRooms ? adjustedPackageGroups : adjustedPackageGroups.slice(0, 9);
  const cunrrencyDefault = login?.account?.currency;
  const intl = useIntl();

  return (
    <>
      <div className="card-body">
        <div className="room-card gap-2">
          {roomsToDisplay?.map((hotel: any, i: number) => {
            const { groupedPackages } = hotel;
            const { rooms } = groupedPackages[0];
            const lowestPricePackage = findLowestPricePackage(groupedPackages);

            if (!lowestPricePackage) {
              return null;
            }

            const formatter = formatMoneyCurrency(lowestPricePackage.currency);

            const { account, userWalletData } = login;
            const { selectedHotelSearchClientCash } = hotelsStore;
            const maxWalletClientCashInt = lowestPricePackage.maxWalletClientCash;
            const hotelPrices = getWalletPrices(account as IAccount, userWalletData as IUserWallet, selectedHotelSearchClientCash, lowestPricePackage.price, maxWalletClientCashInt as number, WalletPriceType.Details);
            const displayWalletSavings = account?.walletWalletSavings && (hotelPrices?.priceSavings > 0 || hotelPrices?.maxClientCashAmountAllow > 0) && !!hotelPrices?.maxClientCashAmountAllow;

            const currency = lowestPricePackage.currency ? lowestPricePackage.currency : cunrrencyDefault;
            const marginatorPriceFinal = displayWalletSavings ? hotelPrices?.priceFinalMax : hotelPrices?.priceFinal;
            const imageUrl = findRoomImage(lowestPricePackage?.rooms[0].targetRoomKey);
            const isDefaultImage = imageUrl === defaultRoomImage;

            const amenities = findAmenities(rooms[0])?.amenities;
            const amenitiesToDisplay = amenities?.slice(0, 4).join(', ');

            const showGoldenBorder = shouldShowGoldenBorder(lowestPricePackage);
            const showGrayBorder = shouldShowGrayBorder(lowestPricePackage);

            return (
              <div key={i} className={`card border bg-transparent p-3 ${showGoldenBorder ? 'golden-border' : ''} ${showGrayBorder ? 'gray-border' : ''}`}>
                <div className="row g-3 g-md-4">
                  <div className="col-md-4">
                    <div className="position-relative overflow-hidden">
                      <img
                        src={imageUrl}
                        className="card-img custom-dashboard-card-image"
                        alt="Room"
                      />
                      {isDefaultImage && (
                        <span className="actualTxt position-absolute text-white ">
                          <FormattedMessage id="room.not.image" />
                        </span>
                      )}
                      <div className="position-absolute top-0 start-0 z-index-1 m-2">
                        <div className={`badge ${showGoldenBorder ? 'text-bg-warning' : showGrayBorder ? 'text-bg-secondary' : 'text-bg-success'}`}>
                          <FormattedMessage id="save" /> {Math.floor(lowestPricePackage.savingPercent)}%
                        </div>
                      </div>

                      {/* Loyalty Reward Label - only show when golden border is active */}
                      {showGoldenBorder && (
                        <div className="position-absolute top-0 start-0 z-index-1 m-2">
                          <div className="badge text-bg-warning loyalty-reward-badge">
                            <FormattedMessage id="loyalty.reward" defaultMessage="Loyalty reward" />
                          </div>
                        </div>
                      )}

                      {/* Premium Badge Icon - only show when golden border is active */}
                      {showGoldenBorder && (
                        <div className="position-absolute top-0 start-0 z-index-1 m-2">
                          <div className="badge text-bg-warning premium-badge-icon">
                            <svg width="23" height="23" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              {/* Medal circle with scalloped border */}
                              <path d="M12 2C13.5 2 15 2.5 16.2 3.6C17.4 4.7 18 6.2 18 7.8C18 9.4 17.4 10.9 16.2 12C15 13.1 13.5 13.6 12 13.6C10.5 13.6 9 13.1 7.8 12C6.6 10.9 6 9.4 6 7.8C6 6.2 6.6 4.7 7.8 3.6C9 2.5 10.5 2 12 2Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                              {/* Inner circle */}
                              <circle cx="12" cy="7.8" r="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
                              {/* Five-pointed star in center */}
                              <path d="M12 5.8L12.8 7.2L14.2 7.4L13.2 8.6L13.4 10L12 9.2L10.6 10L10.8 8.6L9.8 7.4L11.2 7.2L12 5.8Z" fill="currentColor" />
                              {/* Ribbons hanging down */}
                              <path d="M10 13.6L9 15.6L11 16.6L13 15.6L12 13.6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                              <path d="M14 13.6L15 15.6L13 16.6L11 15.6L12 13.6" stroke="currentColor" strokeWidth="1.5" fill="none" />
                              {/* Sparkle effects */}
                              <path d="M4 6L5 6.5L4.5 7.5L3.5 7L4 6Z" stroke="currentColor" strokeWidth="1" fill="none" />
                              <path d="M20 6L19 6.5L19.5 7.5L20.5 7L20 6Z" stroke="currentColor" strokeWidth="1" fill="none" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-8">
                    <div className="card-body d-flex flex-column p-0 h-100 justify-content-between">
                      <Tooltip title={lowestPricePackage.rooms[0].roomName}>
                        <h5 className="card-title mb-0 line-clamp">
                          {lowestPricePackage.rooms[0].roomName}
                        </h5>
                      </Tooltip>

                      <div className="pt-1">
                        <span>
                          <span>
                            {findBedType(rooms[0])?.bedTypes.length > 0 ? (
                              <ul>
                                {findBedType(rooms[0]).bedTypes.map((bedType: any, index: number) => (
                                  <li key={index}>{bedType}</li>))}
                              </ul>) : null}
                          </span>

                          <div className="amenities-container-dashboard">
                            <p>
                              {!isEmpty(amenitiesToDisplay) ? (
                                <Tooltip title={amenitiesToDisplay}>
                                  {amenitiesToDisplay}
                                </Tooltip>) : <>&nbsp;</>}
                            </p>

                            {findAmenities(rooms[0])?.amenities.length > 7 && (
                              <>
                                <button
                                  className="btn btn-link p-0 mb-0 pt-3 more"
                                  onClick={() => toggleDropdown(findAmenities(rooms[0])?.roomKey)}
                                >
                                  <FormattedMessage id="more..." />
                                </button>

                                {showDropdown === findAmenities(rooms[0])?.roomKey && (
                                  <div className="dropdown-more" ref={dropdownRef}>
                                    {findAmenities(rooms[0])
                                      ?.amenities.slice(4)
                                      .map((amenity: any, index: number) => {
                                        return (
                                          <div key={index} className="amenities-dropdown">
                                            {amenity}
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </>)}

                          </div>
                        </span>
                      </div>

                      {lowestPricePackage.refundability === 'nonrefundable' && (
                        <ul className="list-group list-group-borderless small mb-0 mt-1">
                          <li className="list-group-item d-flex text-danger p-0">
                            <i className="bi bi-patch-check-fill me-2"></i>
                            <FormattedMessage id="hotel.non.refundable" />
                          </li>
                        </ul>
                      )}

                      {lowestPricePackage.refundability === 'refundable' && (
                        <ul className="list-group list-group-borderless small mb-0 mt-1">
                          <li className="list-group-item d-flex text-success p-0">
                            <i className="bi bi-patch-check-fill me-2"></i>
                            <FormattedMessage id="hotel.refundable" />
                          </li>
                        </ul>
                      )}

                      <div className="d-flex align-items-start justify-content-between mt-2">
                        <div className="small max-width">
                          {!account?.isMLM ? (
                            <WalletMessage
                              price={lowestPricePackage.price}
                              maxClientCash={maxWalletClientCashInt}
                              currency={lowestPricePackage.currency as string}
                              type={WalletPriceType.Details}
                              isOneLine
                              isShort
                              hideApplied={displayWalletSavings}
                              greenText={displayWalletSavings}
                              style={{ paddingRight: '15px', marginBottom: '15px' }}
                            />) : null}

                          <div className="align-self-end">
                            <span className="me-2 small-text-price"><FormattedMessage id="result.hotel.card.public" /></span>
                            <span className="fw-light text-decoration-line-through small-text-price">
                              {formatter.format(
                                findLowestPricePackage(lowestPricePackage.publicPrices).price,
                              )}
                            </span>
                            <h6 className="mb-0 text-success fw-medium pe-2">
                              {formatter.format(lowestPricePackage.pricePerNight)}
                              <span className="mb-0 me-2 small-text-price">/<FormattedMessage id="night" /></span>
                            </h6>

                          </div>
                        </div>

                        <div className="d-flex align-items-center flex-column">
                          <div className="d-flex align-items-center justify-content-end">
                            <div className="small max-width text-end">
                              {/* Price difference without loyalty - only show when golden border is active */}
                              {showGoldenBorder && (
                                <div className="ms-2">
                                  {renderPriceDifference(lowestPricePackage)}
                                </div>
                              )}
                              <span className="fw-bold fs-6 text-black">
                                <Currency currency={currency as string} />{renderNumber(marginatorPriceFinal, 2)}
                              </span>
                              <p className='mb-2'>
                                {' '}
                                for{' '}
                                {Math.round(
                                  lowestPricePackage.price / lowestPricePackage.pricePerNight,
                                )}{' '}
                                {Math.round(
                                  lowestPricePackage.price / lowestPricePackage.pricePerNight,
                                ) > 1
                                  ? (intl.formatMessage({ id: 'nights.title' }))
                                  : (intl.formatMessage({ id: 'night' }))}{' '}
                                {intl.formatMessage({ id: 'rooms.night.incl.taxes.only' })}
                              </p>
                            </div>
                          </div>
                          <BlueButton
                            type="primary"
                            className={`btn btn-sm btn-primary mb-0 ms-auto fs-13 ${showGoldenBorder ? 'golden-border' : ''} ${showGrayBorder ? 'gray-border' : ''}`}
                            disabled={showGrayBorder}
                            onClick={() =>
                              handleSubmit(lowestPricePackage.packageId, lowestPricePackage.hotelId, hotelPrices)
                            }
                          >
                            <FormattedMessage id="select.room" />
                          </BlueButton>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {!showAllRooms && adjustedPackageGroups.length > roomsToDisplay.length && (
        <div className="text-center mt-sm-5 mt-3 mb-sm-0 mb-3">
          <button className="btn btn-outline-primary" onClick={() => setShowAllRooms(true)}>
            <FormattedMessage id="hotel.show.more.rooms" />
          </button>
        </div>
      )}
    </>
  );
};

export default DashboardCards;